import useFuelingService from '../../../hooks/fueling-services/useFuelingService.ts';
import useUpdateFuelingServiceMetadata from '../../../hooks/fueling-services/useUpdateFuelingServiceMetadata.ts';
import { UpdateFuelingServiceMetadataRequest } from '../../../api/requests/fueling-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';
import { LoadingOverlay, Modal } from '@mantine/core';
import MutateFuelingServiceFuelingInfoForm, {
  MutateFuelingServiceFuelingInfoFormValues,
} from '../../ui/places/MutateFuelingServiceFuelingInfoForm.tsx';
import { useMutation } from '@tanstack/react-query';
import { ErrorResponse } from '../../../api/responses/error-response.ts';

interface UpdateFuelingServiceFuelingInfoModalProps {
  opened: boolean;
  onClose: () => void;
  serviceId: string;
  onSuccess?: () => void;
}

const UpdateFuelingServiceFuelingInfoModal = ({
  opened,
  onClose,
  serviceId,
  onSuccess,
}: UpdateFuelingServiceFuelingInfoModalProps) => {
  const { data: fuelingService, isLoading: isFuelingServiceLoading } =
    useFuelingService(serviceId);

  const { mutateAsync: updateFuelingServiceMetadata } =
    useUpdateFuelingServiceMetadata();

  const {
    mutate: handleSubmit,
    isPending: isUpdateFuelingServicesMetadataPending,
  } = useMutation<
    void,
    ErrorResponse,
    MutateFuelingServiceFuelingInfoFormValues
  >({
    mutationFn: async (formValues) => {
      const updateFuelingServiceMetadataRequest: UpdateFuelingServiceMetadataRequest =
        {
          airPumpInfo: formValues.airPumpInfo,
          brandId: fuelingService.brandId ?? '',
          carMaintenanceInfo: formValues.carMaintenanceInfo,
          carWashInfo: formValues.carWashInfo,
          convenienceStoreInfo: formValues.convenienceStoreInfo,
          hasAccessibleRestroom: formValues.hasAccessibleRestroom,
          hasRestroom: formValues.hasRestroom,
          name: fuelingService.name,
          notes: fuelingService.notes,
          ownershipType: formValues.ownershipType ?? 'UNKNOWN',
          parkingLotInfo: formValues.parkingLotInfo,
          phoneNumber: fuelingService.phoneNumber,
          pricingSource: fuelingService.pricingSource,
          selfServiceTypes: formValues.selfServiceTypes,
          serviceId: serviceId,
          supplier: formValues.supplier,
          supportedFuelTypes: formValues.supportedFuelTypes,
          waterPumpInfo: formValues.waterPumpInfo,
          website: fuelingService.website,
        };

      await updateFuelingServiceMetadata(updateFuelingServiceMetadataRequest);
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      notifyError('更新加油資訊失敗', error.message);
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="編輯加油資訊"
      centered
      closeOnClickOutside={false}
      size={659}
      styles={{
        title: { fontWeight: 700, fontSize: 16 },
        header: { paddingBottom: 24 },
      }}
    >
      {isFuelingServiceLoading ? (
        <LoadingOverlay visible />
      ) : (
        <MutateFuelingServiceFuelingInfoForm
          initialValues={{
            supplier: fuelingService.supplier,
            ownershipType: fuelingService.ownershipType,
            supportedFuelTypes: fuelingService.supportedFuelTypes,
            selfServiceTypes: fuelingService.selfServiceTypes,
            hasRestroom: fuelingService.hasRestroom,
            hasAccessibleRestroom: fuelingService.hasAccessibleRestroom,
            airPumpInfo: fuelingService.airPumpInfo,
            parkingLotInfo: fuelingService.parkingLotInfo,
            carWashInfo: fuelingService.carWashInfo,
            carMaintenanceInfo: fuelingService.carMaintenanceInfo,
            convenienceStoreInfo: fuelingService.convenienceStoreInfo,
            waterPumpInfo: fuelingService.waterPumpInfo,
          }}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isUpdateFuelingServicesMetadataPending}
          submitButtonText={'更新'}
        />
      )}
    </Modal>
  );
};

export { UpdateFuelingServiceFuelingInfoModal };
